import { getLocalTimeZone } from "@internationalized/date";
import React from "react";
import {
  type SelectProps,
  SelectStateContext,
  SelectValue,
} from "react-aria-components";

import { getZoneAbbr } from "@/lib/utils/tz";
import { Select, SelectItem, SelectList, SelectTriggerButton } from "./select";

type TzItem = { id: string; label: string; abbr: string };

const allTimezones = Intl.supportedValuesOf("timeZone");
const allTimezonesEnriched: TzItem[] = allTimezones.map((tz) => ({
  id: tz,
  label: tz,
  abbr: getZoneAbbr(tz),
}));

const localTimezone = getLocalTimeZone();

export type TzSelectProps = SelectProps & {
  selectableTimezones?: string[];
  zoneMapping?: Record<string, string>;
  display?: "full" | "abbr";
};

export function TzSelect({
  selectableTimezones = allTimezones,
  zoneMapping,
  display = "full",
  ...props
}: TzSelectProps) {
  const options: TzItem[] = React.useMemo(() => {
    return allTimezonesEnriched.filter((tz) =>
      selectableTimezones?.includes(tz.id),
    );
  }, [selectableTimezones]);

  const abbrById = React.useMemo(() => {
    const map: Record<string, string> = {};
    for (const tz of options) map[tz.id] = tz.abbr;
    return map;
  }, [options]);

  return (
    <Select {...props} placeholder="Tz" aria-label="Time zone">
      {/* Custom trigger for TzSelect: abbreviation when closed, full item when open */}
      <CustomTzTrigger abbrById={abbrById} />
      <SelectList
        items={options}
        className="max-h-[400px] w-[390px] overflow-y-auto"
      >
        {(item) => (
          <SelectItem id={item.id} textValue={item.abbr}>
            <div className="flex items-center justify-between gap-2 w-full">
              <span>{zoneMapping?.[item.id] ?? item.label}</span>
              <span className="text-xs text-muted-foreground">{item.abbr}</span>
            </div>
          </SelectItem>
        )}
      </SelectList>
    </Select>
  );
}

function CustomTzTrigger({ abbrById }: { abbrById: Record<string, string> }) {
  const state = React.useContext(SelectStateContext);
  return (
    <SelectTriggerButton>
      <SelectValue className="flex-1 text-xs data-placeholder:text-muted-foreground/70 [&_[slot=description]]:hidden truncate">
        {({ isPlaceholder, selectedItem }) => {
          const key = state?.selectedKey as string | null | undefined;
          if (isPlaceholder || !key) return null;
          // Always render abbreviation in the trigger to avoid layout shift
          const abbr = key ? abbrById[key] : undefined;
          if (abbr) return <>{abbr}</>;
          if (selectedItem) {
            const node = selectedItem as unknown as {
              textValue?: string;
              rendered?: React.ReactNode;
            };
            return <>{node.textValue ?? node.rendered ?? null}</>;
          }
          return null;
        }}
      </SelectValue>
    </SelectTriggerButton>
  );
}
